import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserPlus,
  User,
  Briefcase,
  Languages,
  Award,
  CalendarClock,
  ShieldCheck,
  ArrowRight,
  Plus,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Check,
  FileCheck,
  Upload,
  X,
  Loader,
  FileText,
  Send,
  Eye,
  EyeOff,
  CheckCircle2,
  Mic,
  Layers,
} from 'lucide-react';
import { authApi, adminApi, translatorApi, fileUrl } from '../api/client';
import DashFormStepper from '../components/dashboard/DashFormStepper';
import PhotoUpload from '../components/dashboard/PhotoUpload';
import FileUpload from '../components/dashboard/FileUpload';
import { allCountries, quoteLanguages } from '../data/quoteFormOptions';
import { useCatalogLanguages } from '../data/subServicesStore';
import { flagEmoji, langFlagUrl } from '../utils/langFlags';
import LangFlagSelect from '../components/common/LangFlagSelect';
import { getVilleOptions } from '../data/geoData';
import '../components/dashboard/DashForm.css';
import './AuthPage.css';

const langLabels = {
  ar: 'Arabe',
  fr: 'Français',
  en: 'Anglais',
  it: 'Italien',
  de: 'Allemand',
  es: 'Espagnol',
  ru: 'Russe',
  pt: 'Portugais',
  tr: 'Turc',
  zh: 'Chinois',
  other: 'Autre',
};

const TRANSLATION_TYPES = [
  'Traduction professionnelle', 'Traduction assermentée', 'Traduction certifiée',
];

// Sous-ensemble « officiel » des services (habilitation ou validation admin requise)
const OFFICIAL_SERVICE_TYPES = ['Traduction assermentée', 'Traduction certifiée', 'Légalisation', 'Apostille'];

const INTERPRETING_TYPES = [
  'Interprétation consécutive', 'Interprétation de liaison', 'Interprétation simultanée',
  'Interprétation chuchotée', 'Interprétation judiciaire', 'Interprétation médicale',
  'Interprétation de conférence', 'Autre',
];

const AVAILABILITY_OPTIONS = [
  { value: 'immediat', label: 'Disponible immédiatement' },
  { value: 'sur_demande', label: 'Disponible sur demande' },
  { value: 'indisponible_temporaire', label: 'Indisponible temporairement' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Actif' },
  { value: 'pending', label: 'En attente' },
  { value: 'inactive', label: 'Inactif' },
];

const WORK_MODES = [
  { value: 'onsite', label: 'Présentiel' },
  { value: 'remote', label: 'À distance' },
  { value: 'both', label: 'Les deux' },
];

const PROFESSIONAL_TYPES = [
  { value: 'translator', label: 'Traducteur' },
  { value: 'interpreter', label: 'Interprète' },
  { value: 'both', label: 'Traducteur & Interprète' },
];


const MISSION_TYPES = [
  'Réunion', 'Conférence', 'Tribunal', 'Rendez-vous médical', 'Rendez-vous administratif',
  'Entreprise', 'Événement', 'Autre',
];

const INTERPRETING_MODES = ['Présentiel', 'Visioconférence', 'Téléphone'];

const LEVELS = ['Langue maternelle', 'Professionnel', 'Courant', 'Intermédiaire'];

const DELAYS = [
  { value: 'standard', label: 'Standard' },
  { value: 'rapide', label: 'Rapide' },
  { value: 'urgent', label: 'Urgent' },
];

const CIVILITIES = ['Mme', 'M.', 'Autre'];

const WEEK_DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

function fileName(path) {
  if (!path) return '';
  return path.split('/').pop() || path;
}

function isImage(path) {
  if (!path) return false;
  return /\.(jpg|jpeg|png|webp|gif)$/i.test(path);
}

function MultiFileUpload({ values = [], onChange, accept = '.pdf,.doc,.docx,image/*' }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const res = await authApi.uploadFile(file);
      if (res.url) onChange?.([...values, res.url]);
    } catch (err) {
      console.error(err);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const removeAt = (i) => onChange?.(values.filter((_, idx) => idx !== i));

  return (
    <div className="file-upload">
      <input
        type="file"
        ref={inputRef}
        accept={accept}
        onChange={handleFile}
        style={{ display: 'none' }}
      />
      {values.map((url, i) => (
        <div className="file-upload-preview" key={`${url}-${i}`}>
          {isImage(url) ? (
            <img
              src={fileUrl(url)}
              alt="Aperçu du document"
              style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8 }}
            />
          ) : (
            <FileText size={28} color="var(--teal)" />
          )}
          <a href={fileUrl(url) || '#'} target="_blank" rel="noreferrer">
            {fileName(url)}
          </a>
          <button type="button" onClick={() => removeAt(i)} aria-label="Supprimer">
            <X size={14} />
          </button>
        </div>
      ))}
      <button
        type="button"
        className="photo-upload-zone"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
      >
        {busy ? <Loader size={16} className="spin" /> : <Upload size={16} />}
        {busy ? 'Chargement…' : 'Cliquer pour ajouter un fichier'}
      </button>
    </div>
  );
}

function generatePassword(length = 12) {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const special = '!@#$%^&*()_+-=[]{}|;:,.<>?';
  const all = upper + lower + digits + special;
  let pwd = '';
  pwd += upper[Math.floor(Math.random() * upper.length)];
  pwd += lower[Math.floor(Math.random() * lower.length)];
  pwd += digits[Math.floor(Math.random() * digits.length)];
  pwd += special[Math.floor(Math.random() * special.length)];
  for (let i = 4; i < length; i += 1) {
    pwd += all[Math.floor(Math.random() * all.length)];
  }
  return pwd.split('').sort(() => Math.random() - 0.5).join('');
}

export default function TranslatorRegisterPage() {
  const navigate = useNavigate();
  // Langues actives du catalogue (API) — repli sur la liste statique hors-ligne
  const { languages: catalogLangs } = useCatalogLanguages();
  const langOptions = catalogLangs.length
    ? [...catalogLangs.map((l) => l.code.toLowerCase()), 'other']
    : quoteLanguages;
  const langMeta = (code) => {
    const lc = String(code || '').toLowerCase();
    const found = catalogLangs.find((l) => l.code.toLowerCase() === lc);
    return {
      name: found?.nameFr || langLabels[lc] || code,
      flagUrl: langFlagUrl(found),
      emoji: flagEmoji(found?.flag),
    };
  };
  const langLabel = (code) => {
    const m = langMeta(code);
    return [m.emoji, m.name].filter(Boolean).join(' ');
  };
  const searchParams = new URLSearchParams(window.location.search);
  const embedded = searchParams.has('embed');
  const editId = searchParams.get('edit');
  // Mode « self » : le traducteur connecté modifie son propre profil
  const selfMode = searchParams.has('self');
  const editMode = Boolean(editId || selfMode);
  const adminMode = !selfMode && (embedded || Boolean(editId));

  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState('idle');
  const [showPwd, setShowPwd] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);
  const [success, setSuccess] = useState('');
  const [attemptedSteps, setAttemptedSteps] = useState({});

  const [form, setForm] = useState({
    professionalType: 'translator',
    civility: '',
    firstName: '',
    lastName: '',
    photoUrl: '',
    professionalEmail: '',
    phone: '',
    country: 'TN',
    city: 'Tunis',
    address: '',
    experienceYears: '',
    bio: '',
    cvUrl: '',
    languageCombos: [{ source: '', target: '', level: '' }],
    translationServiceTypes: [],
    accreditationCountry: '',
    accreditationBody: '',
    accreditationNumber: '',
    accreditationValidity: '',
    accreditationJustificationUrl: '',
    interpretingServices: [],
    interpretingMissionTypes: [],
    interpretingModes: [],
    availabilityStatus: 'disponible',
    availabilityOptions: ['immediat'],
    availabilityNotes: '',
    availableDays: [],
    availabilityFrom: '',
    availabilityTo: '',
    usualDelay: 'standard',
    workMode: 'both',
    diplomas: [],
    professionalCertificates: [],
    certifications: [],
    attestations: [],
    otherDocuments: [],
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false,
    acceptPrivacy: false,
    certifyAccurate: false,
    acceptVerification: false,
    status: 'active',
  });

  const isTranslator = form.professionalType === 'translator' || form.professionalType === 'both';
  const isInterpreter = form.professionalType === 'interpreter' || form.professionalType === 'both';
  const swornSelected = (form.translationServiceTypes || []).includes('Traduction assermentée');

  // Cascade Pays → Ville (geoData)
  const villeOptions = useMemo(() => getVilleOptions(form.country), [form.country]);

  const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || '');

  const passwordChecks = (v) => ({
    length: (v || '').length >= 8,
    upper: /[A-Z]/.test(v || ''),
    lower: /[a-z]/.test(v || ''),
    digit: /\d/.test(v || ''),
    special: /[^A-Za-z0-9]/.test(v || ''),
  });
  const isStrongPassword = (v) => Object.values(passwordChecks(v)).every(Boolean);

  useEffect(() => {
    if (editMode || !isEmail(form.email)) {
      setEmailStatus('idle');
      return undefined;
    }
    setEmailStatus('checking');
    const t = setTimeout(async () => {
      try {
        const res = await authApi.checkEmail(form.email);
        setEmailStatus(res.exists ? 'taken' : 'free');
      } catch {
        setEmailStatus('idle');
      }
    }, 500);
    return () => clearTimeout(t);
  }, [form.email, editMode]);

  // Remonter en haut du formulaire à chaque changement d'étape
  // (protégé : un échec de scroll ne doit jamais casser l'affichage de l'étape)
  const formTopRef = useRef(null);
  useEffect(() => {
    try {
      formTopRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      try {
        formTopRef.current?.scrollIntoView();
        window.scrollTo(0, 0);
      } catch { /* noop */ }
    }
  }, [step]);
  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateCombo = (i, key, value) => {
    setForm((prev) => ({
      ...prev,
      languageCombos: (prev.languageCombos || []).map((c, idx) => {
        if (idx !== i) return c;
        const patch = { ...c, [key]: value };
        if (patch.source && patch.target && patch.source === patch.target) {
          if (key === 'source') {
            patch.target = langOptions.find((l) => l !== value) || value;
          } else {
            patch.source = langOptions.find((l) => l !== value) || value;
          }
        }
        return patch;
      }),
    }));
  };

  const addCombo = () => {
    setForm((prev) => ({
      ...prev,
      languageCombos: [...(prev.languageCombos || []), { source: '', target: '', level: '' }],
    }));
  };

  const removeCombo = (i) => {
    setForm((prev) => ({
      ...prev,
      languageCombos: (prev.languageCombos || []).filter((_, idx) => idx !== i),
    }));
  };

  const toggle = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: (prev[field] || []).includes(value)
        ? (prev[field] || []).filter((v) => v !== value)
        : [...(prev[field] || []), value],
    }));
  };

  const checkboxGroup = (field, options, list) => (
    <div className="dform-checkbox-scroll">
      {options.map((opt) => (
        <label key={opt} className="dform-checkbox">
          <input
            type="checkbox"
            checked={(list || []).includes(opt)}
            onChange={() => toggle(field, opt)}
          />
          {opt}
        </label>
      ))}
    </div>
  );

  const sectionValid = {
    type: Boolean(form.professionalType),
    personal: Boolean(form.civility && form.lastName && form.firstName && isEmail(form.professionalEmail) && form.phone && form.country && form.city),
    profile: Boolean(form.experienceYears !== '' && form.bio),
    languages:
      form.languageCombos.length > 0 &&
      form.languageCombos.every((c) => c.source && c.target && c.level && c.source !== c.target) &&
      Boolean(form.cvUrl),
    translator:
      form.translationServiceTypes.length > 0 &&
      (!swornSelected ||
        Boolean(
          form.accreditationCountry &&
          form.accreditationBody &&
          form.accreditationNumber &&
          form.accreditationJustificationUrl
        )),
    interpreter:
      form.interpretingServices.length > 0 &&
      form.interpretingMissionTypes.length > 0 &&
      form.interpretingModes.length > 0,
    availability: Boolean((form.availabilityOptions || []).length > 0 && form.workMode),
    documents: true,
    account: Boolean(
      adminMode
        ? isEmail(form.email) && form.status && (editId || emailStatus !== 'taken')
        : editMode
          ? isEmail(form.email)
          : isEmail(form.email) && emailStatus !== 'taken' && isStrongPassword(form.password) && form.password === form.confirmPassword && form.acceptTerms && form.acceptPrivacy && form.certifyAccurate
    ),
    recap: true,
  };

  const steps = [
    { key: 'identity', label: 'Profil & identité' },
    { key: 'languages', label: 'Langues & expérience' },
    ...(isTranslator ? [{ key: 'translation', label: 'Services de traduction' }] : []),
    ...(isInterpreter ? [{ key: 'interpretation', label: "Services d'interprétariat" }] : []),
    { key: 'disponibilite', label: 'Disponibilité' },
    { key: 'documents', label: 'Documents' },
    { key: 'compte', label: adminMode ? 'Validation' : 'Compte & validation' },
  ];

  const totalSteps = steps.length;
  const activeKey = steps[step - 1]?.key;
  const stepValid = {
    identity: sectionValid.type && sectionValid.personal && sectionValid.profile,
    languages: sectionValid.languages,
    translation: isTranslator ? sectionValid.translator : true,
    interpretation: isInterpreter ? sectionValid.interpreter : true,
    disponibilite: sectionValid.availability,
    documents: sectionValid.documents,
    compte: sectionValid.account && sectionValid.recap,
  };

  // En mode édition (admin ou profil du traducteur), navigation libre entre les étapes
  const canNext = () => (editMode ? true : Boolean(stepValid[activeKey]));
  const canSubmit = () => Object.values(stepValid).every(Boolean);

  useEffect(() => {
    if (editMode) return;
    if (!attemptedSteps[activeKey]) {
      setError('');
      return;
    }
    if (!canNext()) {
      setError(stepError(activeKey) || 'Veuillez remplir tous les champs obligatoires.');
    } else {
      setError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptedSteps, activeKey, form]);

  const missingFields = (key) => {
    const m = [];
    if (key === 'identity') {
      if (!form.professionalType) m.push('Type de professionnel');
      if (!form.civility) m.push('Civilité');
      if (!form.lastName) m.push('Nom');
      if (!form.firstName) m.push('Prénom');
      if (!form.professionalEmail) m.push('Email professionnel (obligatoire)');
      else if (!isEmail(form.professionalEmail)) m.push('Email professionnel invalide (ex. nom@domaine.com)');
      if (!form.phone) m.push('Téléphone');
      if (!form.country) m.push('Pays');
      if (!form.city) m.push('Ville');
      if (form.experienceYears === '') m.push("Années d'expérience");
      if (!form.bio) m.push('Présentation professionnelle');
    }
    if (key === 'languages') {
      const combosOk = form.languageCombos.length > 0 &&
        form.languageCombos.every((c2) => c2.source && c2.target && c2.level && c2.source !== c2.target);
      if (!combosOk) m.push('Paire linguistique valide (source différente de la cible)');
      if (!form.cvUrl) m.push('CV');
    }
    if (key === 'translation' && isTranslator) {
      if (!form.translationServiceTypes.length) m.push('Services de traduction');
      if (swornSelected) {
        if (!form.accreditationCountry) m.push("Pays d'habilitation");
        if (!form.accreditationBody) m.push('Autorité / organisme');
        if (!form.accreditationNumber) m.push("Numéro d'habilitation");
        if (!form.accreditationJustificationUrl) m.push("Justificatif d'habilitation");
      }
    }
    if (key === 'interpretation' && isInterpreter) {
      if (!form.interpretingServices.length) m.push("Types d'interprétariat");
      if (!form.interpretingMissionTypes.length) m.push('Types de missions');
      if (!form.interpretingModes.length) m.push("Modalités d'interprétation");
    }
    if (key === 'disponibilite') {
      if (!(form.availabilityOptions || []).length) m.push('Disponibilité générale');
      if (!form.workMode) m.push('Modalités de travail');
    }
    if (key === 'compte') {
      if (!form.email) m.push('Email (obligatoire)');
      else if (!isEmail(form.email)) m.push('Email invalide (ex. nom@domaine.com)');
      else if (!editMode && emailStatus === 'taken') m.push('Email déjà utilisé');
      if (adminMode) {
        if (!form.status) m.push('Statut du profil');
      } else if (!editMode) {
        if (!isStrongPassword(form.password)) m.push('Mot de passe (8+ car., majuscule, minuscule, chiffre, symbole)');
        else if (form.password !== form.confirmPassword) m.push('Confirmation du mot de passe identique');
        if (!form.acceptTerms) m.push('Acceptation des conditions générales');
        if (!form.acceptPrivacy) m.push('Acceptation de la politique de confidentialité');
        if (!form.certifyAccurate) m.push('Certification des informations fournies');
      }
    }
    return m;
  };

  const stepError = (key) => {
    const missing = missingFields(key);
    return missing.length ? 'Champs à compléter : ' + missing.join(', ') + '.' : '';
  };

  const goToStep = (n) => {
    if (editMode) {
      setError('');
      setStep(n);
      return;
    }
    if (n <= step) {
      setError('');
      setStep(n);
      return;
    }
    const firstInvalid = steps.slice(0, n - 1).find((s) => !stepValid[s.key]);
    if (firstInvalid) {
      setAttemptedSteps((prev) => ({ ...prev, [firstInvalid.key]: true }));
      setError(stepError(firstInvalid.key) || 'Veuillez compléter les étapes précédentes.');
      setStep(steps.findIndex((s) => s.key === firstInvalid.key) + 1);
      return;
    }
    setError('');
    setStep(n);
  };

  const goNext = () => {
    if (!canNext()) {
      setAttemptedSteps((prev) => ({ ...prev, [activeKey]: true }));
      setError(stepError(activeKey) || 'Veuillez remplir tous les champs obligatoires.');
      return;
    }
    setError('');
    setStep((s) => Math.min(s + 1, totalSteps));
  };

  const goPrev = () => {
    setError('');
    setStep((s) => Math.max(s - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAttemptedSteps(Object.fromEntries(steps.map((s2) => [s2.key, true])));
    if (!editMode && !canSubmit()) {
      const firstInvalid = steps.find((s2) => !stepValid[s2.key]);
      setError((firstInvalid && stepError(firstInvalid.key)) || 'Veuillez remplir tous les champs obligatoires.');
      if (firstInvalid) setStep(steps.findIndex((s2) => s2.key === firstInvalid.key) + 1);
      return;
    }
    setLoading(true);
    setError('');

    const fullName = [form.firstName, form.lastName].filter(Boolean).join(' ').trim();
    const comboLangs = (form.languageCombos || []).flatMap((c) => [c.source, c.target]);
    const sourceLanguages = Array.from(new Set((form.languageCombos || []).map((c) => c.source)));
    const targetLanguages = Array.from(new Set((form.languageCombos || []).map((c) => c.target)));
    const availOpts = form.availabilityOptions || [];
    const availabilityStatus = availOpts.includes('immediat')
      ? 'disponible'
      : availOpts.includes('sur_demande')
        ? 'sur_demande'
        : availOpts.includes('indisponible_temporaire')
          ? 'indisponible'
          : 'disponible';
    const officialSelected = (form.translationServiceTypes || []).filter((t) =>
      OFFICIAL_SERVICE_TYPES.includes(t)
    );

    const payload = {
      name: fullName || form.email,
      email: form.email,
      password: form.password,
      confirmPassword: form.confirmPassword,
      phone: form.phone,
      city: form.city,
      country: form.country,
      acceptTerms: form.acceptTerms,
      acceptPrivacy: form.acceptPrivacy,
      ...(adminMode ? { isActive: form.status === 'active' } : {}),
      languages: Array.from(new Set(comboLangs)),
      translatorProfile: {
        civility: form.civility,
        firstName: form.firstName,
        lastName: form.lastName,
        photoUrl: form.photoUrl,
        professionalEmail: form.professionalEmail,
        country: form.country,
        city: form.city,
        address: form.address,
        bio: form.bio,
        experienceYears: Number(form.experienceYears) || 0,
        cvUrl: form.cvUrl,
        professionalType: form.professionalType,
        sourceLanguages,
        targetLanguages,
        languageCombos: form.languageCombos || [],
        translationServices: isTranslator ? form.translationServiceTypes : [],
        offersOfficial: isTranslator && officialSelected.length > 0,
        officialServices: isTranslator ? officialSelected : [],
        accredited: swornSelected,
        accreditationCountry: swornSelected ? form.accreditationCountry : '',
        accreditationBody: swornSelected ? form.accreditationBody : '',
        accreditationNumber: swornSelected ? form.accreditationNumber : '',
        accreditationValidity: swornSelected ? form.accreditationValidity : '',
        accreditationJustificationUrl: swornSelected ? form.accreditationJustificationUrl : '',
        interpretingServices: isInterpreter ? form.interpretingServices : [],
        interpretingMissionTypes: isInterpreter ? form.interpretingMissionTypes : [],
        interpretingModes: isInterpreter ? form.interpretingModes : [],
        workMode: form.workMode,
        availabilityStatus,
        availabilityOptions: availOpts,
        availabilityNotes: form.availabilityNotes,
        availableDays: form.availableDays || [],
        availabilityFrom: form.availabilityFrom || '',
        availabilityTo: form.availabilityTo || '',
        usualDelay: form.usualDelay,
        acceptedDelays: form.usualDelay ? [form.usualDelay] : [],
        acceptsUrgent: form.usualDelay === 'urgent',
        diplomas: form.diplomas || [],
        professionalCertificates: form.professionalCertificates || [],
        certifications: form.certifications || [],
        attestations: form.attestations || [],
        otherDocuments: form.otherDocuments || [],
        acceptVerification: form.certifyAccurate || form.acceptVerification,
        certifyAccurate: form.certifyAccurate,
        ...(adminMode ? { status: form.status || 'active' } : editMode ? {} : { status: 'pending' }),
      },
    };

    try {
      if (editId) {
        await adminApi.updateTranslator(editId, {
          name: payload.name,
          isActive: payload.isActive,
          civility: form.civility,
          firstName: form.firstName,
          lastName: form.lastName,
          address: form.address,
          phone: payload.phone,
          city: payload.city,
          country: payload.country,
          languages: payload.languages,
          translatorProfile: payload.translatorProfile,
        });
        setSuccess('Profil mis à jour avec succès.');
        setTimeout(() => setSuccess(''), 4000);
        setLoading(false);
        try { window.parent.postMessage('translatorUpdated', window.location.origin); } catch { /* noop */ }
      } else if (selfMode) {
        await translatorApi.updateProfile({
          name: payload.name,
          civility: form.civility,
          firstName: form.firstName,
          lastName: form.lastName,
          address: form.address,
          phone: payload.phone,
          city: payload.city,
          country: payload.country,
          languages: payload.languages,
          translatorProfile: payload.translatorProfile,
        });
        setSuccess('Profil mis à jour avec succès.');
        setTimeout(() => setSuccess(''), 4000);
        setLoading(false);
        try { window.parent.postMessage('translatorUpdated', window.location.origin); } catch { /* noop */ }
      } else if (embedded) {
        await adminApi.createTranslator(payload);
        setSuccess('Compte traducteur créé.');
        setLoading(false);
        try {
          window.parent.postMessage(
            { type: 'translatorSaved', email: form.email, password: form.password },
            window.location.origin,
          );
        } catch { /* noop */ }
      } else {
        await authApi.registerTranslator(payload);
        setSuccess("Votre candidature a été envoyée avec succès. Elle sera examinée par l'équipe Tarjemlek. Vous recevrez une notification dès que votre profil sera validé.");
        setTimeout(() => navigate('/login'), 2800);
      }
    } catch (err) {
      setError(err.data?.message || err.data?.error || err.message || "Erreur lors de l'inscription");
      setLoading(false);
    }
  };

  const StepType = () => (
    <div className="dform-row">
      <section className="dform-section">
        <header className="dform-section-head">
          <Briefcase size={17} />
          <h4>Vous souhaitez exercer en tant que :</h4>
        </header>
        <div className="dform-field dform-wide">
          <div className="dform-choices">
            {PROFESSIONAL_TYPES.map((opt) => {
              const ChoiceIcon = { translator: FileText, interpreter: Mic, both: Layers }[opt.value] || Briefcase;
              const desc = {
                translator: 'Traduction de documents écrits',
                interpreter: 'Missions orales, en présentiel ou à distance',
                both: 'Traduction et interprétariat',
              }[opt.value];
              return (
                <label key={opt.value} className="dform-choice">
                  <input
                    type="radio"
                    name="professionalType"
                    value={opt.value}
                    checked={form.professionalType === opt.value}
                    onChange={(e) => setField('professionalType', e.target.value)}
                    required
                  />
                  <span className="dform-choice-icon"><ChoiceIcon size={22} /></span>
                  <span className="dform-choice-text">
                    <strong>{opt.label}</strong>
                    <small>{desc}</small>
                  </span>
                </label>
              );
            })}
          </div>
          <p className="dform-hint">
            Le choix effectué détermine les sections affichées dans la suite du formulaire.
          </p>
        </div>
      </section>
    </div>
  );

  const StepPersonal = () => (
    <div className="dform-row">
      <section className="dform-section">
        <header className="dform-section-head">
          <User size={17} />
          <h4>Informations personnelles</h4>
        </header>
        <div className="dform-grid dform-grid--2">
          <div className="dform-field">
            <label>Civilité <span className="dform-req">*</span></label>
            <select value={form.civility} required onChange={(e) => setField('civility', e.target.value)}>
              <option value="">Sélectionner</option>
              {form.civility && !CIVILITIES.includes(form.civility) && (
                <option value={form.civility}>{form.civility}</option>
              )}
              {CIVILITIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="dform-field">
            <label>Nom <span className="dform-req">*</span></label>
            <input required value={form.lastName} onChange={(e) => setField('lastName', e.target.value)} placeholder="Ben Salem" />
          </div>
          <div className="dform-field">
            <label>Prénom <span className="dform-req">*</span></label>
            <input required value={form.firstName} onChange={(e) => setField('firstName', e.target.value)} placeholder="Ahmed" />
          </div>
          <div className="dform-field">
            <label>Email professionnel <span className="dform-req">*</span></label>
            <input type="email" required value={form.professionalEmail} onChange={(e) => setField('professionalEmail', e.target.value)} placeholder="contact@exemple.tn" />
          </div>
          <div className="dform-field">
            <label>Téléphone <span className="dform-req">*</span></label>
            <input required value={form.phone} onChange={(e) => setField('phone', e.target.value)} placeholder="+216 00 000 000" />
          </div>
          <div className="dform-field">
            <label>Pays <span className="dform-req">*</span></label>
            <select
              required
              value={form.country}
              onChange={(e) => {
                                setForm((prev) => ({ ...prev, country: e.target.value, city: '' }));
              }}
            >
              <option value="">Sélectionnez un pays</option>
              {allCountries.map(({ code, name }) => (
                <option key={code} value={code}>{name}</option>
              ))}
            </select>
          </div>
          <div className="dform-field">
            <label>Ville <span className="dform-req">*</span></label>
            {villeOptions.length ? (
              <select
                required
                value={form.city}
                onChange={(e) => {
                                    setForm((prev) => ({ ...prev, city: e.target.value }));
                }}
              >
                <option value="">Sélectionnez une ville</option>
                {villeOptions.map((v) => (
                  <option key={v.stateCode || v.name} value={v.name}>{v.name}</option>
                ))}
                {form.city && !villeOptions.some((v) => v.name === form.city) && (
                  <option value={form.city}>{form.city}</option>
                )}
              </select>
            ) : (
              <input required value={form.city} onChange={(e) => setField('city', e.target.value)} placeholder="Tunis" />
            )}
          </div>
          <div className="dform-field">
            <label>Adresse</label>
            <input value={form.address} onChange={(e) => setField('address', e.target.value)} placeholder="Rue, code postal..." />
          </div>
          <div className="dform-field dform-wide">
            <label>Photo de profil</label>
            <PhotoUpload value={form.photoUrl} onChange={(url) => setField('photoUrl', url)} uploadApi={authApi.uploadFile} />
          </div>
        </div>
      </section>
    </div>
  );

  const StepProfile = () => (
    <div className="dform-row">
      <section className="dform-section dform-section--profile">
        <header className="dform-section-head">
          <Award size={17} />
          <h4>Expérience professionnelle</h4>
        </header>
        <div className="dform-grid dform-grid--2">
          <div className="dform-field">
            <label>Années d&apos;expérience <span className="dform-req">*</span></label>
            <div className="dform-suffix">
              <input type="number" min="0" max="60" required value={form.experienceYears} onChange={(e) => setField('experienceYears', e.target.value)} placeholder="5" />
              <span className="dform-suffix-label">ans</span>
            </div>
          </div>
          <div className="dform-field">
            <label>Présentation professionnelle <span className="dform-req">*</span></label>
            <textarea
              rows={5}
              required
              maxLength={2000}
              value={form.bio}
              onChange={(e) => setField('bio', e.target.value)}
              placeholder="Présentez brièvement votre parcours, vos compétences et votre expérience."
            />
            <div className="dform-field-foot">
              <p className="dform-example">
                Exemple : « Traducteur français-arabe spécialisé dans les domaines juridique et administratif, avec 5 ans d'expérience. »
              </p>
              <span className="dform-count">{form.bio.length} / 2000</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );

  const StepLanguages = () => (
    <div className="dform-row dform-row--lang-cv">
      <section className="dform-section">
        <header className="dform-section-head">
          <Languages size={17} />
          <h4>Paires linguistiques maîtrisées <span className="dform-req">*</span></h4>
        </header>
        <div className="dform-field dform-wide">
          <p className="dform-hint">
            Ajoutez chaque combinaison séparément. Ces informations sont utilisées pour mettre en relation les professionnels avec les demandes des clients.
          </p>
          <div className="dform-repeat">
            {(form.languageCombos || []).map((c, i) => (
              <div className="dform-lang-row" key={i}>
                <LangFlagSelect
                  value={c.source}
                  onChange={(v) => updateCombo(i, 'source', v)}
                  options={langOptions.filter((l) => l !== c.target)}
                  meta={langMeta}
                  placeholder="Langue source"
                  ariaLabel="Langue source"
                />
                <span className="dform-repeat-arrow"><ArrowRight size={14} /></span>
                <LangFlagSelect
                  value={c.target}
                  onChange={(v) => updateCombo(i, 'target', v)}
                  options={langOptions.filter((l) => l !== c.source)}
                  meta={langMeta}
                  placeholder="Langue cible"
                  ariaLabel="Langue cible"
                />
                <select value={c.level} onChange={(e) => updateCombo(i, 'level', e.target.value)} required aria-label="Niveau de maîtrise">
                  <option value="">Niveau</option>
                  {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
                <button type="button" className="dform-icon-btn" onClick={() => removeCombo(i)} aria-label="Supprimer la paire">
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
            <button type="button" className="dform-add-btn" onClick={addCombo}>
              <Plus size={15} />
              Ajouter une autre paire linguistique
            </button>
          </div>
          <p className="dform-example" style={{ marginTop: 8 }}>
            Exemple : Arabe → Français — Professionnel · Français → Anglais — Courant
          </p>
        </div>
      </section>

      <section className="dform-section">
        <header className="dform-section-head">
          <FileCheck size={17} />
          <h4>CV <span className="dform-req">*</span></h4>
        </header>
        <div className="dform-field dform-wide">
          <FileUpload value={form.cvUrl} onChange={(url) => setField('cvUrl', url)} accept=".pdf,.doc,.docx,image/*" uploadApi={authApi.uploadFile} />
          <p className="dform-hint">Formats acceptés : PDF, DOC, DOCX ou image.</p>
        </div>
      </section>
    </div>
  );

  const StepTranslator = () => (
    <div className="dform-row">
      <section className="dform-section">
        <header className="dform-section-head">
          <FileCheck size={17} />
          <h4>Services de traduction proposés <span className="dform-req">*</span></h4>
        </header>
        <div className="dform-field dform-wide">
          {checkboxGroup('translationServiceTypes', TRANSLATION_TYPES, form.translationServiceTypes)}
        </div>
        {swornSelected && (
          <div className="dform-grid dform-grid--2 dform-subblock" style={{ marginTop: 12 }}>
            <div className="dform-field">
              <label>Pays d&apos;habilitation <span className="dform-req">*</span></label>
              <select required value={form.accreditationCountry} onChange={(e) => setField('accreditationCountry', e.target.value)}>
                <option value="">Sélectionnez un pays</option>
                {allCountries.map(({ code, name }) => (
                  <option key={code} value={code}>{name}</option>
                ))}
              </select>
            </div>
            <div className="dform-field">
              <label>Autorité / organisme <span className="dform-req">*</span></label>
              <input required value={form.accreditationBody} onChange={(e) => setField('accreditationBody', e.target.value)} placeholder="Cour de cassation, Ministère..." />
            </div>
            <div className="dform-field">
              <label>Numéro d&apos;habilitation <span className="dform-req">*</span></label>
              <input required value={form.accreditationNumber} onChange={(e) => setField('accreditationNumber', e.target.value)} placeholder="N° d'habilitation" />
            </div>
            <div className="dform-field">
              <label>Date de validité (si applicable)</label>
              <input type="date" value={form.accreditationValidity} onChange={(e) => setField('accreditationValidity', e.target.value)} />
            </div>
            <div className="dform-field dform-wide">
              <label>Justificatif d&apos;habilitation <span className="dform-req">*</span></label>
              <FileUpload value={form.accreditationJustificationUrl} onChange={(url) => setField('accreditationJustificationUrl', url)} accept=".pdf,.doc,.docx,image/*" uploadApi={authApi.uploadFile} />
            </div>
          </div>
        )}
      </section>
    </div>
  );

  const StepInterpreter = () => (
    <div className="dform-row">
      <section className="dform-section">
        <header className="dform-section-head">
          <Languages size={17} />
          <h4>Types d&apos;interprétariat <span className="dform-req">*</span></h4>
        </header>
        <div className="dform-field dform-wide">
          {checkboxGroup('interpretingServices', INTERPRETING_TYPES, form.interpretingServices)}
        </div>
      </section>

      <section className="dform-section">
        <header className="dform-section-head">
          <Briefcase size={17} />
          <h4>Types de missions <span className="dform-req">*</span></h4>
        </header>
        <div className="dform-field dform-wide">
          {checkboxGroup('interpretingMissionTypes', MISSION_TYPES, form.interpretingMissionTypes)}
        </div>
      </section>

      <section className="dform-section">
        <header className="dform-section-head">
          <CalendarClock size={17} />
          <h4>Mode de prestation <span className="dform-req">*</span></h4>
        </header>
        <div className="dform-field dform-wide">
          {checkboxGroup('interpretingModes', INTERPRETING_MODES, form.interpretingModes)}
        </div>
      </section>
    </div>
  );

  const StepAvailability = () => (
    <div className="dform-row">
      <section className="dform-section">
        <header className="dform-section-head">
          <CalendarClock size={17} />
          <h4>Disponibilité générale <span className="dform-req">*</span></h4>
        </header>
        <div className="dform-field dform-wide">
          <div className="dform-radio-group">
            {AVAILABILITY_OPTIONS.map((opt) => (
              <label key={opt.value} className="dform-radio">
                <input
                  type="radio"
                  name="availabilityGeneral"
                  checked={(form.availabilityOptions || [])[0] === opt.value}
                  onChange={() => setField('availabilityOptions', [opt.value])}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>
      </section>

      <section className="dform-section">
        <header className="dform-section-head">
          <CalendarClock size={17} />
          <h4>Modalités de travail <span className="dform-req">*</span></h4>
        </header>
        <div className="dform-field dform-wide">
          <div className="dform-radio-group">
            {WORK_MODES.map((opt) => (
              <label key={opt.value} className="dform-radio">
                <input
                  type="radio"
                  name="workMode"
                  checked={form.workMode === opt.value}
                  onChange={() => setField('workMode', opt.value)}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>
      </section>

      <section className="dform-section">
        <header className="dform-section-head">
          <CalendarClock size={17} />
          <h4>Jours & horaires disponibles</h4>
        </header>
        <div className="dform-grid dform-grid--2">
          <div className="dform-field dform-wide">
            <label>Jours disponibles</label>
            <div className="dform-checkbox-scroll">
              {WEEK_DAYS.map((d) => (
                <label key={d} className="dform-checkbox">
                  <input
                    type="checkbox"
                    checked={(form.availableDays || []).includes(d)}
                    onChange={() => toggle('availableDays', d)}
                  />
                  {d}
                </label>
              ))}
            </div>
          </div>
          <div className="dform-field">
            <label>Heure de début</label>
            <input type="time" value={form.availabilityFrom} onChange={(e) => setField('availabilityFrom', e.target.value)} />
          </div>
          <div className="dform-field">
            <label>Heure de fin</label>
            <input type="time" value={form.availabilityTo} onChange={(e) => setField('availabilityTo', e.target.value)} />
          </div>

        </div>
      </section>

      <section className="dform-section">
        <header className="dform-section-head">
          <CalendarClock size={17} />
          <h4>Délai habituel de réalisation</h4>
        </header>
        <div className="dform-field dform-wide">
          <div className="dform-radio-group">
            {DELAYS.map((d) => (
              <label key={d.value} className="dform-radio">
                <input
                  type="radio"
                  name="usualDelay"
                  checked={form.usualDelay === d.value}
                  onChange={() => setField('usualDelay', d.value)}
                />
                {d.label}
              </label>
            ))}
          </div>
        </div>
      </section>
    </div>
  );

  const StepDocuments = () => (
    <div className="dform-row">
      <section className="dform-section">
        <header className="dform-section-head">
          <FileCheck size={17} />
          <h4>Documents à fournir</h4>
        </header>
        <div className="dform-grid dform-grid--2">
          <div className="dform-field">
            <label>Diplômes</label>
            <MultiFileUpload values={form.diplomas} onChange={(urls) => setField('diplomas', urls)} />
          </div>
          <div className="dform-field">
            <label>Certificats professionnels</label>
            <MultiFileUpload values={form.professionalCertificates} onChange={(urls) => setField('professionalCertificates', urls)} />
          </div>
          <div className="dform-field">
            <label>Attestations d&apos;expérience</label>
            <MultiFileUpload values={form.attestations} onChange={(urls) => setField('attestations', urls)} />
          </div>
          <div className="dform-field">
            <label>Certifications professionnelles</label>
            <MultiFileUpload values={form.certifications} onChange={(urls) => setField('certifications', urls)} />
          </div>
          <div className="dform-field">
            <label>Autres justificatifs</label>
            <MultiFileUpload values={form.otherDocuments} onChange={(urls) => setField('otherDocuments', urls)} />
          </div>
          {swornSelected && (
            <div className="dform-field">
              <label>Justificatif d&apos;habilitation <span className="dform-req">*</span></label>
              <FileUpload value={form.accreditationJustificationUrl} onChange={(url) => setField('accreditationJustificationUrl', url)} accept=".pdf,.doc,.docx,image/*" uploadApi={authApi.uploadFile} />
            </div>
          )}
        </div>
        <p className="dform-hint">
          Le CV est demandé à l&apos;étape « Langues & expérience ».
          {swornSelected ? " Le justificatif d'habilitation est obligatoire pour le service « Traduction assermentée »." : ''}
        </p>
      </section>
    </div>
  );

  const StepAccount = () => (
    <div className="dform-row">
      <section className="dform-section dform-section--ghost">
        <header className="dform-section-head">
          <ShieldCheck size={17} />
          <h4>Création du compte</h4>
        </header>
        <div className="dform-grid dform-grid--2">
          <div className="dform-field dform-wide">
            <label>Email <span className="dform-req">*</span></label>
            <input type="email" required value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="traducteur@exemple.tn" disabled={editMode} />
            {emailStatus === 'checking' && <p className="dform-hint">Vérification de l&apos;email…</p>}
            {emailStatus === 'taken' && <p className="dform-hint dform-hint--error">Cet email est déjà utilisé.</p>}
            {emailStatus === 'free' && <p className="dform-hint dform-hint--ok">Email disponible.</p>}
          </div>
          {!editMode && (<>
          <div className="dform-field">
            <label>Mot de passe <span className="dform-req">*</span></label>
            <div className="dform-pwd">
              <input type={showPwd ? 'text' : 'password'} required value={form.password} onChange={(e) => setField('password', e.target.value)} placeholder="Min. 8 caractères" />
              <button type="button" className="dform-pwd-toggle" onClick={() => setShowPwd((v) => !v)} aria-label="Afficher le mot de passe">
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {form.password && (
              <ul className="pwd-rules">
                {[
                  ['length', '8 caractères min.'],
                  ['upper', '1 majuscule'],
                  ['lower', '1 minuscule'],
                  ['digit', '1 chiffre'],
                  ['special', '1 symbole'],
                ].map(([k, lbl]) => (
                  <li key={k} className={passwordChecks(form.password)[k] ? 'ok' : ''}>{lbl}</li>
                ))}
              </ul>
            )}
          </div>
          <div className="dform-field">
            <label>Confirmation du mot de passe <span className="dform-req">*</span></label>
            <div className="dform-pwd">
              <input type={showPwd2 ? 'text' : 'password'} required value={form.confirmPassword} onChange={(e) => setField('confirmPassword', e.target.value)} placeholder="Répétez le mot de passe" />
              <button type="button" className="dform-pwd-toggle" onClick={() => setShowPwd2((v) => !v)} aria-label="Afficher la confirmation">
                {showPwd2 ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {form.confirmPassword && form.password !== form.confirmPassword && (
              <p className="dform-hint dform-hint--error">Les mots de passe ne correspondent pas.</p>
            )}
          </div>
          </>)}
          <div className="dform-field dform-wide">
            <label className="dform-check">
              <input type="checkbox" checked={form.acceptTerms} onChange={(e) => setField('acceptTerms', e.target.checked)} />
              J&apos;accepte les conditions générales. <span className="dform-req">*</span>
            </label>
          </div>
          <div className="dform-field dform-wide">
            <label className="dform-check">
              <input type="checkbox" checked={form.acceptPrivacy} onChange={(e) => setField('acceptPrivacy', e.target.checked)} />
              J&apos;accepte la politique de confidentialité. <span className="dform-req">*</span>
            </label>
          </div>
          <div className="dform-field dform-wide">
            <label className="dform-check">
              <input type="checkbox" checked={form.certifyAccurate} onChange={(e) => setField('certifyAccurate', e.target.checked)} />
              Je certifie que les informations fournies sont exactes. <span className="dform-req">*</span>
            </label>
          </div>
        </div>
      </section>
    </div>
  );

  const typeLabel = {
    translator: 'Traducteur',
    interpreter: 'Interprète',
    both: 'Traducteur & Interprète',
  }[form.professionalType];

  const availabilityLabel =
    (form.availabilityOptions || [])
      .map((v) => AVAILABILITY_OPTIONS.find((o) => o.value === v)?.label || v)
      .join(', ') || '—';

  const workModeLabel = WORK_MODES.find((o) => o.value === form.workMode)?.label || form.workMode || '—';

  const StepRecap = () => (
    <div className="dform-row">
      <section className="dform-section dform-section--recap">
        <header className="dform-section-head">
          <Check size={17} />
          <h4>Récapitulatif de candidature</h4>
        </header>
        <div className="dform-grid dform-grid--2">
          <div className="dform-field dform-wide">
            <p><strong>Type :</strong> {typeLabel}</p>
            <p><strong>Nom :</strong> {[form.civility, form.firstName, form.lastName].filter(Boolean).join(' ')}</p>
            <p><strong>Langues :</strong>{' '}
              {(form.languageCombos || [])
                .map((c) => `${langLabel(c.source)} → ${langLabel(c.target)} (${c.level})`)
                .join(', ') || '—'}
            </p>
            {isTranslator && (
              <p><strong>Services de traduction :</strong> {(form.translationServiceTypes || []).join(', ') || '—'}</p>
            )}
            {isInterpreter && (
              <>
                <p><strong>Types d&apos;interprétariat :</strong> {(form.interpretingServices || []).join(', ') || '—'}</p>
                <p><strong>Types de missions :</strong> {(form.interpretingMissionTypes || []).join(', ') || '—'}</p>
                <p><strong>Mode de prestation :</strong> {(form.interpretingModes || []).join(', ') || '—'}</p>
              </>
            )}
            <p><strong>Localisation :</strong>{' '}
              {[
                form.city,
                allCountries.find((c) => c.code === form.country)?.name || form.country,
              ].filter(Boolean).join(', ') || '—'}
            </p>
            <p><strong>Disponibilité :</strong> {availabilityLabel}</p>
            <p><strong>Modalités de travail :</strong> {workModeLabel}</p>
            <p><strong>Jours :</strong> {(form.availableDays || []).join(', ') || '—'}</p>
            {(form.availabilityFrom || form.availabilityTo) ? (
              <p><strong>Horaires :</strong> {form.availabilityFrom || '—'} → {form.availabilityTo || '—'}</p>
            ) : null}
            {adminMode && (
              <p><strong>Statut :</strong> {STATUS_OPTIONS.find((o) => o.value === form.status)?.label || '—'}</p>
            )}
          </div>
        </div>
        {!adminMode && (
          <p className="dform-hint">
            Après l&apos;envoi, votre candidature recevra le statut « En attente de validation » et sera examinée par un administrateur.
          </p>
        )}
      </section>
    </div>
  );

  const StepIdentity = () => (<>{StepType()}{StepPersonal()}{StepProfile()}</>);
  const StepValidation = () => (
    <div className="dform-row">
      <section className="dform-section dform-section--ghost">
        <header className="dform-section-head">
          <ShieldCheck size={17} />
          <h4>Validation du profil</h4>
        </header>
        <div className="dform-grid dform-grid--2">
          <div className="dform-field dform-wide">
            <label>Email du compte <span className="dform-req">*</span></label>
            <input type="email" required value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="traducteur@exemple.tn" disabled={editMode} />
            {emailStatus === 'checking' && <p className="dform-hint">Vérification de l&apos;email…</p>}
            {emailStatus === 'taken' && <p className="dform-hint dform-hint--error">Cet email est déjà utilisé.</p>}
            {emailStatus === 'free' && <p className="dform-hint dform-hint--ok">Email disponible.</p>}
          </div>
          <div className="dform-field dform-wide">
            <label>Statut du profil <span className="dform-req">*</span></label>
            <div className="dform-radio-group">
              {STATUS_OPTIONS.map((opt) => (
                <label key={opt.value} className="dform-radio">
                  <input
                    type="radio"
                    name="adminStatus"
                    checked={form.status === opt.value}
                    onChange={() => setField('status', opt.value)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
        </div>
        {!editId && (
          <p className="dform-hint" style={{ marginTop: 10 }}>
            Un mot de passe est généré automatiquement : les identifiants seront envoyés au professionnel par email.
          </p>
        )}
      </section>
    </div>
  );

  const StepCompte = () => (<>{adminMode ? StepValidation() : StepAccount()}{StepRecap()}</>);

  const stepComponents = {
    identity: StepIdentity,
    languages: StepLanguages,
    translation: StepTranslator,
    interpretation: StepInterpreter,
    disponibilite: StepAvailability,
    documents: StepDocuments,
    compte: StepCompte,
  };

  const ActiveStep = stepComponents[activeKey] || StepType;



  useEffect(() => {
    if (!editMode) return;
    const load = editId
      ? adminApi.getTranslator(editId).then((res) => res.translator)
      : translatorApi.profile().then((res) => res.user);
    load.then((tr) => {
      tr = tr || {};
      const pr = tr.translatorProfile || {};
      const arr = (v) => (Array.isArray(v) ? v : (v ? [v] : []));
      const combos = arr(pr.languageCombos?.length ? pr.languageCombos : pr.langPairs)
        .filter((x) => x && typeof x === 'object')
        .map((x) => ({
          source: String(x.source || 'ar').toLowerCase(),
          target: String(x.target || 'fr').toLowerCase(),
          level: x.level || 'Courant',
        }))
        .map((c) => ({
          ...c,
          target: c.source === c.target ? (langOptions.find((l) => l !== c.source) || c.target) : c.target,
        }));
      setForm((prev) => ({
        ...prev,
        professionalType: pr.professionalType || 'translator',
        civility: pr.civility || tr.civility || '',
        photoUrl: pr.photoUrl || '',
        lastName: pr.lastName || '',
        firstName: pr.firstName || tr.firstName || '',
        professionalEmail: pr.professionalEmail || '',
        phone: tr.phone || '',
        country: pr.country || tr.country || 'TN',
        city: tr.city || pr.city || '',
        experienceYears: pr.experienceYears ?? '',
        bio: pr.bio || '',
        cvUrl: pr.cvUrl || '',
        diplomas: arr(pr.diplomas),
        professionalCertificates: arr(pr.professionalCertificates),
        certifications: arr(pr.certifications),
        languageCombos: combos.length ? combos : [{ source: '', target: '', level: '' }],
        translationServiceTypes: [...new Set([...arr(pr.translationServices), ...arr(pr.officialServices)])],
        accreditationCountry: pr.accreditationCountry || '',
        accreditationBody: pr.accreditationBody || '',
        accreditationNumber: pr.accreditationNumber || '',
        accreditationValidity: pr.accreditationValidity || '',
        accreditationJustificationUrl: pr.accreditationJustificationUrl || '',
        interpretingServices: arr(pr.interpretingServices),
        interpretingMissionTypes: arr(pr.interpretingMissionTypes),
        interpretingModes: arr(pr.interpretingModes),
        workMode: pr.workMode || 'both',
        availabilityStatus: pr.availabilityStatus || 'disponible',
        availabilityOptions: arr(pr.availabilityOptions).length
          ? arr(pr.availabilityOptions)
          : ({ disponible: ['immediat'], sur_demande: ['sur_demande'], indisponible: ['indisponible_temporaire'], en_mission: ['indisponible_temporaire'], partiellement_disponible: ['sur_demande'] }[pr.availabilityStatus] || []),
        availabilityType: pr.availabilityType || 'freelance',
        availabilityNotes: pr.availabilityNotes || '',
        availableDays: arr(pr.availableDays),
        availabilityFrom: pr.availabilityFrom || '',
        availabilityTo: pr.availabilityTo || '',
        usualDelay: pr.usualDelay || 'standard',
        attestations: arr(pr.attestations),
        otherDocuments: arr(pr.otherDocuments),
        address: pr.address || '',
        email: tr.email || '',
        acceptTerms: true,
        acceptPrivacy: true,
        certifyAccurate: true,
        acceptVerification: true,
        status: pr.status || (tr.isActive === false ? 'pending' : 'active'),
      }));
    }).catch(() => setError('Impossible de charger ce profil.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, selfMode]);

  useEffect(() => {
    if (embedded && !editMode) {
      const generated = generatePassword();
      setForm((prev) => ({
        ...prev,
        password: generated,
        confirmPassword: generated,
        acceptTerms: true,
        acceptPrivacy: true,
        certifyAccurate: true,
        acceptVerification: true,
      }));
    }
  }, [embedded, editMode]);

  return (
    <div className={embedded ? 'auth-page auth-page--embed' : 'auth-page'} style={embedded ? { minHeight: 'auto', padding: 0, background: '#fff' } : undefined}>
      {success && (
        <div className="auth-toast" role="status">
          <CheckCircle2 size={20} />
          <span>{success}</span>
        </div>
      )}
      <div
        className="auth-shell auth-shell--translator auth-shell--wide"
        style={embedded ? { display: 'block', minHeight: 'auto', boxShadow: 'none', borderRadius: 0 } : undefined}
      >
        {!embedded && (
        <aside className="auth-brand-panel">
          <div className="auth-brand-glow" aria-hidden="true" />
          <div className="auth-brand-content">
            <p className="auth-brand-name">
              Tarjemlek
              <span lang="ar">ترجملك</span>
            </p>
            <h1>Devenir professionnel</h1>
            <p className="auth-brand-text">Rejoignez notre plateforme en tant que traducteur, interprète ou les deux.</p>
            <ul className="auth-perks">
              <li><UserPlus size={18} aria-hidden="true" /><span>Travaillez avec des clients variés</span></li>
              <li><Languages size={18} aria-hidden="true" /><span>Plus de 170 combinaisons de langues</span></li>
              <li><Award size={18} aria-hidden="true" /><span>Revenus attractifs et flexibles</span></li>
            </ul>
          </div>
        </aside>
        )}

        <div className="auth-form-panel" style={{ maxWidth: '100%' }}>
          {/* Soumission native bloquée : seul le bouton « Mettre à jour » appelle handleSubmit.
              Évite tout enregistrement implicite (Entrée, activation automatique du bouton submit). */}
          <form className={'dform tr-form' + (attemptedSteps[activeKey] ? ' form-attempted' : '')} onSubmit={(e) => e.preventDefault()} ref={formTopRef}>
            <DashFormStepper steps={steps.map((s) => s.label)} current={step} onSelect={goToStep} />

            <div className="tr-progress" aria-hidden="true">
              <div className="tr-progress-track">
                <div className="tr-progress-bar" style={{ width: String(Math.round((step / totalSteps) * 100)) + '%' }} />
              </div>
            </div>

            <div className="dform-pane" key={activeKey} style={{ marginTop: 24 }}>
              {ActiveStep()}
            </div>

            {error && (
              <div className="auth-error" role="alert" style={{ marginTop: 16 }}>
                {error}
              </div>
            )}

            <footer className="dform-footer" style={{ marginTop: 24 }}>
              <span className="dform-progress">Étape {step} / {totalSteps}</span>
              <span className="dform-spacer" />
              {step > 1 && (
                <button type="button" className="btn btn-outline" onClick={goPrev}>
                  <ChevronLeft size={15} /> Précédent
                </button>
              )}
              {step < totalSteps ? (
                <button type="button" className="btn btn-primary" onClick={goNext}>
                  Suivant <ChevronRight size={15} />
                </button>
              ) : (
                <>
                          <button type="button" className="btn btn-primary" disabled={loading} onClick={handleSubmit}>
                    <Send size={15} />
                    {loading ? 'Envoi…' : (editMode ? 'Mettre à jour le profil' : adminMode ? 'Créer le professionnel' : 'Soumettre ma candidature')}
                  </button>
                </>
              )}
            </footer>
          </form>
        </div>
      </div>
    </div>
  );
}
